/**
 * =============================================================================
 * WlAddonCountsTrendChart
 *
 * Gráfico evolutivo de 12 meses de "Novos add-ons vs. Add-ons cancelados",
 * espelhando visualmente o `WlTenantsTrendChart` da Visão geral (mesma paleta
 * roxo/âmbar, mesmo comportamento de toggle bar/line, mesma tooltip).
 *
 * Como o WlTenantsTrendChart lê os campos `new_tenants` e `churned_tenants`,
 * este componente re-mapeia os campos `new_addons` / `canceled_addons` do
 * payload de add-ons para as mesmas chaves — evitando duplicar o gráfico.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (aba Add-ons — WL Financeiro).
 * =============================================================================
 */
import { useMemo } from 'react';
import { WlTenantsTrendChart } from './WlTenantsTrendChart';
import type { ChartTypeMode } from './ChartTypeToggle';
import type { WlAddonCountsPoint } from '@/hooks/wl/useWlAddonKpis';

interface Props {
  data: WlAddonCountsPoint[];
  mode?: ChartTypeMode;
  onModeChange?: (mode: ChartTypeMode) => void;
}

/** Evolução 12 meses: novos vs. cancelados (add-ons). */
export function WlAddonCountsTrendChart({ data, mode, onModeChange }: Props) {
  // Remapeia os campos para os aliases esperados pelo WlTenantsTrendChart.
  const mapped = useMemo(
    () =>
      (data ?? []).map((p) => ({
        month: p.month,
        new_tenants: Number(p.new_addons || 0),
        churned_tenants: Number(p.canceled_addons || 0),
        active_total: Number(p.active_total || 0),
      })),
    [data],
  );

  return (
    <WlTenantsTrendChart
      data={mapped as any}
      mode={mode}
      onModeChange={onModeChange}
    />
  );
}
